import { useEffect } from 'react';

/**
 * useTiltEffect - Adds 3D tilt effect on mouse hover
 * @param {React.RefObject} ref - Reference to the element
 * @param {object} options - Configuration options
 * @param {number} options.intensity - Tilt intensity (default: 25)
 * @param {boolean} options.disabled - Disable the effect
 */
const useTiltEffect = (ref, options = {}) => {
    const { intensity = 25, disabled = false } = options;

    useEffect(() => {
        const element = ref?.current;
        if (!element || disabled) return;

        const handleMouseMove = (e) => {
            const rect = element.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            const rotateX = (y - centerY) / intensity;
            const rotateY = (centerX - x) / intensity;

            element.style.transform = `perspective(1000px) rotateX(${-rotateX}deg) rotateY(${rotateY}deg) translateY(-4px)`;
        };

        const handleMouseLeave = () => {
            element.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0)';
            element.style.transition = 'transform var(--duration-base) var(--ease-out)';
        };

        const handleMouseEnter = () => {
            element.style.transition = 'none';
        };

        element.addEventListener('mousemove', handleMouseMove);
        element.addEventListener('mouseleave', handleMouseLeave);
        element.addEventListener('mouseenter', handleMouseEnter);

        return () => {
            element.removeEventListener('mousemove', handleMouseMove);
            element.removeEventListener('mouseleave', handleMouseLeave);
            element.removeEventListener('mouseenter', handleMouseEnter);
        };
    }, [ref, intensity, disabled]);
};

export default useTiltEffect;
